import { useEffect, useState, useContext, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Star, ArrowRight, Sparkles, Leaf, Flame, CircleDot, Package, Wine, MapPin, Award, ShieldCheck, Truck, Store } from 'lucide-react';
import { AuthContext } from '@/App';

const HeroCarouselCatalog = lazy(() => import('@/components/HeroCarouselCatalog'));

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_luxhemp/artifacts/eosdqbph_310C9C52-31F3-41A9-B491-B40A0F9BDE7A_L0_001-1_6_2026%2C%2012_08_40%20AM%20%281%29.png';

/* User-uploaded promo banners */
const PREROLLS_BANNER = 'https://customer-assets-jt897jd0.emergentagent.net/job_702eb32d-946e-4f98-99fe-6a5343905257/artifacts/ecyesbxm_ChatGPT%20Image%20Aug%206%2C%202026%2C%2011_28_14%20AM.png';
const FLOWER_BANNER = 'https://customer-assets-jt897jd0.emergentagent.net/job_702eb32d-946e-4f98-99fe-6a5343905257/artifacts/pz6hh5w3_FLOWER.png';
const DAILY_DEALS_BANNER = 'https://customer-assets-jt897jd0.emergentagent.net/job_702eb32d-946e-4f98-99fe-6a5343905257/artifacts/lm1dc9m5_DAILY%20DEALS.jpg';
const GUMMIES_BANNER = 'https://customer-assets-jt897jd0.emergentagent.net/job_702eb32d-946e-4f98-99fe-6a5343905257/artifacts/95iwa0op_HOUSE%20GUMMIES.png';
const DRINKS_HIGHER = 'https://customer-assets-jt897jd0.emergentagent.net/job_702eb32d-946e-4f98-99fe-6a5343905257/artifacts/vvgvvs3f_ChatGPT%20Image%20Aug%206%2C%202026%2C%2001_12_09%20PM.png';
const DRINKS_LIGHTER = 'https://customer-assets-jt897jd0.emergentagent.net/job_702eb32d-946e-4f98-99fe-6a5343905257/artifacts/qpn4mz69_ChatGPT%20Image%20Aug%206%2C%202026%2C%2001_23_48%20PM.png';

function HomeBannerImage({ src, alt, label, className, objectFit = 'cover' }) {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return (
      <div
        className={`${className || 'w-full h-full'} flex flex-col items-center justify-center gap-2 px-4`}
        style={{ background: 'linear-gradient(135deg, #1c1c1c 0%, #2a2a2a 50%, #1c1c1c 100%)' }}
        data-testid="banner-fallback"
      >
        <p className="font-anton text-3xl md:text-5xl uppercase text-ha-ink/85 text-center tracking-wide">{label}</p>
        <p className="font-body text-[10px] md:text-xs uppercase tracking-[0.32em] text-ha-muted">Photo coming soon</p>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setErrored(true)}
      className={className || `w-full h-full object-${objectFit}`}
      loading="lazy"
      decoding="async"
    />
  );
}

const CATEGORIES = [
  { key: 'flower', label: 'Flower', tagline: 'Craft indoor THCa', Icon: Leaf, accent: '#7DB833' },
  { key: 'pre-rolls', label: 'Pre-Rolls', tagline: 'Infused & classic', Icon: Flame, accent: '#8A3FD8' },
  { key: 'concentrates', label: 'Concentrates', tagline: 'Rosin · badder · sugar', Icon: Sparkles, accent: '#7DB833' },
  { key: 'vapes', label: 'Vapes', tagline: '2G magnetic switches', Icon: CircleDot, accent: '#8A3FD8' },
  { key: 'edibles', label: 'Edibles', tagline: 'Precise · small-batch', Icon: Package, accent: '#7DB833' },
  { key: 'drinks', label: 'Drinks', tagline: 'Sessionable elixirs', Icon: Wine, accent: '#8A3FD8' },
];

const REVIEWS = [
  { name: 'Marcus B.', date: 'Feb 2, 2026', rating: 5, text: 'Best flower I\u2019ve had in Asheville — hands down. The Champions tier is worth every dollar.' },
  { name: 'Kayla P.', date: 'Jan 28, 2026', rating: 5, text: 'The staff genuinely knows their stuff. Walked in unsure, walked out with exactly what I needed.' },
  { name: 'David L.', date: 'Jan 25, 2026', rating: 5, text: 'Their infused pre-rolls hit different. Clean burn, incredible terps. My new go-to.' },
  { name: 'Sasha R.', date: 'Jan 20, 2026', rating: 4, text: 'Love the vibe of both locations. COAs on every product — real transparency.' },
  { name: 'Trent M.', date: 'Jan 15, 2026', rating: 5, text: 'Nimbus Snacks is unreal. Prices stay fair, quality stays high. That\u2019s all I ask for.' },
  { name: 'Priya S.', date: 'Jan 10, 2026', rating: 5, text: 'Live rosin here rivals anything I\u2019ve had on either coast. Legit connoisseur-tier.' },
];

function priceFrom(p) {
  if (p.pricing) {
    const values = Array.isArray(p.pricing)
      ? p.pricing.map((x) => x?.price).filter(Boolean)
      : Object.values(p.pricing).filter((v) => typeof v === 'number');
    if (values.length) return Math.min(...values);
  }
  return p.price ?? null;
}

function MarqueeTileImage({ src, alt, name, isGreen, tag = 'marquee' }) {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return (
      <div
        data-testid={`${tag}-product-fallback`}
        className="w-full h-full flex flex-col items-center justify-center gap-2 px-3"
        style={{ background: 'linear-gradient(135deg, #1c1c1c 0%, #2a2a2a 50%, #1c1c1c 100%)' }}
      >
        <Package className="w-10 h-10 opacity-45" style={{ color: isGreen ? '#7DB833' : '#8A3FD8' }} />
        <p className="font-serif text-[13px] text-white/90 text-center leading-tight line-clamp-2">{name}</p>
        <p className="font-body text-[9px] uppercase tracking-[0.24em] text-white/40">Photo coming soon</p>
      </div>
    );
  }
  return (
    <img src={src} alt={alt} loading="lazy" decoding="async" onError={() => setErrored(true)}
      className="w-full h-full object-cover group-hover/card:scale-[1.06] transition-transform duration-700" />
  );
}

function ProductMarquee({ products }) {
  if (!products?.length) return null;
  const track = [...products, ...products];
  return (
    <div className="relative overflow-hidden group ha-marquee-wrap" data-testid="home-marquee">
      <div className="pointer-events-none absolute inset-y-0 left-0 w-32 z-10 bg-gradient-to-r from-ha-bg via-ha-bg/80 to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-32 z-10 bg-gradient-to-l from-ha-bg via-ha-bg/80 to-transparent" />
      <div className="flex gap-6 w-max animate-[marquee_60s_linear_infinite] group-hover:[animation-play-state:paused]">
        {track.map((p, i) => {
          const from = priceFrom(p);
          const isGreen = i % 2 === 0;
          const accent = isGreen ? '#7DB833' : '#8A3FD8';
          return (
            <Link key={`${p.id}-${i}`} to={`/products/${p.id}`} data-testid={`marquee-product-${p.id}-${i}`}
              className="group/card block w-[260px] shrink-0 ha-product-card">
              <div className="relative rounded-2xl overflow-hidden bg-ha-card border border-ha-ink/8 group-hover/card:border-ha-ink/25 transition-colors"
                style={{ boxShadow: `0 20px 50px -25px rgba(0,0,0,0.7), 0 0 0 1px ${accent}22 inset` }}>
                <div className="aspect-[4/5] overflow-hidden bg-ha-bg">
                  <MarqueeTileImage src={p.image} alt={p.name} name={p.name} isGreen={isGreen} tag={`marquee-${p.id}-${i}`} />
                </div>
                {p.tier && (
                  <span className="absolute top-3 left-3 font-body text-[9px] uppercase tracking-[0.24em] px-2.5 py-1 rounded-full bg-ha-ink text-ha-bg font-bold">{p.tier}</span>
                )}
              </div>
              <div className="mt-4 px-1">
                <p className="font-body text-[10px] uppercase tracking-[0.32em] font-bold" style={{ color: accent }}>{p.category || '—'}</p>
                <div className="flex items-baseline justify-between gap-3 mt-1">
                  <h3 className="font-body text-base font-semibold text-ha-ink truncate leading-tight">{p.name}</h3>
                  {from != null && <p className="font-anton text-xl text-ha-ink shrink-0">${from}</p>}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function ReviewCarousel({ reviews }) {
  const track = [...reviews, ...reviews];
  return (
    <div className="relative overflow-hidden group ha-marquee-wrap" data-testid="home-review-carousel">
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10 bg-gradient-to-r from-ha-bg to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10 bg-gradient-to-l from-ha-bg to-transparent" />
      <div className="flex gap-6 w-max animate-[marquee_100s_linear_infinite] group-hover:[animation-play-state:paused] py-6 px-2">
        {track.map((r, i) => (
          <article key={i} data-testid={`home-review-bubble-${i}`}
            className="relative w-[380px] shrink-0 bg-ha-card text-ha-ink p-8 pb-9 border border-ha-ink/10"
            style={{ borderRadius: '32px 32px 32px 8px', boxShadow: '0 20px 50px -20px rgba(0,0,0,0.7)' }}>
            <div className="flex gap-0.5 mb-4">
              {Array.from({ length: 5 }).map((_, k) => (
                <Star key={k} className={`w-4 h-4 ${k < r.rating ? 'fill-ha-green text-ha-green' : 'text-ha-ink/15'}`} />
              ))}
            </div>
            <p className="font-editorial text-xl leading-snug text-ha-ink">&ldquo;{r.text}&rdquo;</p>
            <div className="mt-6 pt-5 border-t border-ha-ink/10 flex justify-between text-[10px] uppercase tracking-[0.28em] text-ha-ink/60 font-body font-semibold">
              <span>{r.name}</span>
              <span>{r.date}</span>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

/* Full-bleed banner card that FILLS the frame (image object-cover) with a title/CTA overlay */
function FlyerBanner({ imgSrc, alt, aspect = '16/9', accent = '#7DB833', href, testId, kicker, title, italicWord, description, cta, ribbon }) {
  return (
    <section className="ha-container ha-cv-auto" data-testid={testId}>
      <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-center">
        <div className="md:col-span-5 space-y-6">
          {kicker && <p className="ha-eyebrow" style={{ color: `${accent}CC` }}>{kicker}</p>}
          <h2 className="ha-display text-ha-ink" style={{ fontSize: 'clamp(2.5rem, 6vw, 5.5rem)' }}>
            {title} {italicWord && <span className="ha-display-italic" style={{ color: accent }}>{italicWord}</span>}
          </h2>
          {description && (
            <p className="font-body text-lg text-ha-ink/70 leading-relaxed max-w-md">{description}</p>
          )}
          {cta && (
            <Link to={cta.to}>
              <button className="ha-btn" style={{ background: accent, color: '#0A0A0A', boxShadow: `0 20px 40px -18px ${accent}` }}
                data-testid={cta.testId}>
                {cta.label}
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          )}
        </div>
        <div className="md:col-span-7">
          <Link to={href} className="block group relative" data-testid={`${testId}-image`}>
            <div className="relative rounded-2xl overflow-hidden border border-ha-ink/10"
              style={{ boxShadow: `0 0 80px -30px ${accent}, 0 40px 80px -30px rgba(0,0,0,0.7)` }}>
              <div className="overflow-hidden bg-ha-bg" style={{ aspectRatio: aspect }}>
                <HomeBannerImage src={imgSrc} alt={alt} label={title}
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-1000" />
              </div>
              {ribbon && <div className="ha-ribbon">{ribbon}</div>}
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  const [belowFoldReady, setBelowFoldReady] = useState(false);

  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    const id = idle(() => setBelowFoldReady(true));
    return () => {
      if (window.cancelIdleCallback && typeof id === 'number') window.cancelIdleCallback(id);
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/products`);
        const visible = res.data.filter((p) => p.visible !== false);
        setProducts(visible);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email: newsletterEmail });
      setNewsletterSubmitted(true);
      const code = res.data?.discountCode || '';
      setTimeout(() => {
        navigate(`/signup?email=${encodeURIComponent(newsletterEmail)}&promo=${encodeURIComponent(code)}`);
      }, 1200);
    } catch (err) {
      setNewsletterSubmitted(true);
      setTimeout(() => {
        navigate(`/signup?email=${encodeURIComponent(newsletterEmail)}`);
      }, 1200);
    }
  };

  return (
    <div className="relative">
      {/* ============ HERO — BIG logo + WORDMARK + MAIN CAROUSEL right below ============ */}
      <section className="relative isolate overflow-hidden" data-testid="home-hero">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[130%] h-[80%] rounded-full opacity-40 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(138,63,216,0.35), transparent 60%)' }} />
          <div className="absolute -bottom-1/2 -right-1/4 w-[80%] h-[80%] rounded-full opacity-35 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(125,184,51,0.35), transparent 60%)' }} />
        </div>

        <div className="ha-container pt-10 md:pt-14 pb-10 md:pb-14">
          <div className="flex items-center justify-between mb-8 opacity-0 animate-ha-fade-up" style={{ animationDelay: '0.05s' }}>
            <p className="ha-eyebrow"><span className="hidden sm:inline">Asheville, NC · </span>Est. 2023</p>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-ha-green animate-pulse" />
              <p className="font-body text-[10px] uppercase tracking-[0.28em] text-ha-ink/60 font-bold">Open · Mon-Sat 9-9 · Sun 10-9</p>
            </div>
          </div>

          {/* Colossal logo — no wordmark text below, logo alone is the hero */}
          <div className="text-center opacity-0 animate-ha-fade-up" style={{ animationDelay: '0.15s' }}>
            <img
              src={LOGO_URL}
              alt="High Achievers"
              width="1400"
              height="1400"
              fetchPriority="high"
              decoding="async"
              className="h-64 sm:h-80 md:h-[26rem] lg:h-[34rem] w-auto mx-auto drop-shadow-[0_0_80px_rgba(125,184,51,0.5)]"
              data-testid="home-hero-logo"
            />
          </div>
        </div>
      </section>

      {/* ============ MAIN CAROUSEL — directly under the wordmark ============ */}
      <section className="ha-container pb-10 md:pb-14" data-testid="home-hero-carousel-wrap">
        <div className="rounded-3xl overflow-hidden border border-ha-ink/10 shadow-editorial-lg" style={{ background: '#000' }}>
          <div className="w-full aspect-[21/9] md:aspect-[21/8]">
            {belowFoldReady && (
              <Suspense fallback={<div className="w-full h-full bg-black" />}>
                <HeroCarouselCatalog />
              </Suspense>
            )}
          </div>
        </div>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/products" data-testid="home-hero-shop">
            <button className="ha-btn ha-btn-green group">
              Shop the Menu
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </Link>
          <Link to="/coas" data-testid="home-hero-coas">
            <button className="ha-btn ha-btn-ghost group">
              View Lab Tests
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </Link>
        </div>
      </section>

      {/* ============ ALL FLOWER — directly below main carousel (first big scroll section) ============ */}
      <FlyerBanner
        testId="home-flower-banner"
        imgSrc={FLOWER_BANNER}
        alt="All Flower — 4G Eighths · 8G Quarter · 16G Halves"
        aspect="16/9"
        accent="#7DB833"
        href="/products?category=flower"
        kicker="Home of the 4 Gram 8th"
        title="All"
        italicWord="flower."
        description="$75 oz on select strains. 4G 1/8ths · 8G quarters · 16G halves. Premium indoor THCa, third-party lab tested."
        cta={{ to: '/products?category=flower', label: 'Shop Flower', testId: 'home-banner-shop-flower' }}
      />

      {/* ============ FEATURED PRODUCTS marquee — between Flower & Pre-Rolls ============ */}
      <section className="mt-12 md:mt-20" data-testid="home-featured">
        <div className="ha-container mb-10">
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="ha-eyebrow mb-4">Every drop on the shelf</p>
              <h2 className="ha-display text-ha-ink" style={{ fontSize: 'clamp(2.5rem, 5.5vw, 5rem)' }}>Featured products.</h2>
            </div>
            <Link to="/products" className="hidden md:inline-flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.28em] text-ha-ink/70 hover:text-ha-green transition-colors font-bold group">
              Shop all <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
        <ProductMarquee products={products} />
      </section>

      {/* ============ PRE-ROLLS ============ */}
      <div className="mt-12 md:mt-20">
        <FlyerBanner
          testId="home-prerolls-banner"
          imgSrc={PREROLLS_BANNER}
          alt="1.2G Infused & Non-Infused Pre-Rolls"
          aspect="16/9"
          accent="#7DB833"
          href="/products?category=pre-rolls"
          kicker="Now Available"
          title="Infused &"
          italicWord="non-infused."
          description="1.2G indoor cannabis pre-rolls in Indica, Sativa & Hybrid. Non-infused $6-$11 · Infused $15."
          cta={{ to: '/products?category=pre-rolls', label: 'Shop Pre-Rolls', testId: 'home-banner-shop-prerolls' }}
        />
      </div>

      {/* ============ STNDRD VAPES with LIVE INTERACTIVE SWITCHER ============ */}
      <section className="relative ha-cv-auto mt-12 md:mt-20" data-testid="home-vapes-banner">
        <div className="ha-container">
          <div className="relative rounded-3xl overflow-hidden shadow-editorial-lg">
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #7DB833 0%, #0A0A0A 45%, #8A3FD8 100%)' }} />
            <div className="ha-grain-surface absolute inset-0 opacity-70" />
            <div className="relative grid md:grid-cols-2 gap-0 items-center">
              <div className="p-8 md:p-16">
                <p className="ha-eyebrow mb-4" style={{ color: 'rgba(255,255,255,0.7)' }}>STNDRD Vapes</p>
                <h2 className="ha-display text-white" style={{ fontSize: 'clamp(3rem, 7vw, 7rem)' }}>
                  2G <span className="ha-display-italic" style={{ color: '#F5F1E8' }}>switches.</span>
                </h2>
                <p className="mt-6 font-body text-lg text-white/90 leading-relaxed max-w-md">
                  One battery. Two flavors. Magnetic dual-cart system. $50 out the door — try the live switch on the flyer.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {['Watermelon × Skywalker', 'Purple Punch × Mimosa', 'Blue Dream × Supernerd', 'SFV OG × Sour Diesel'].map((v) => (
                    <span key={v} className="font-body text-[10px] uppercase tracking-[0.24em] text-white/90 border border-white/30 rounded-full px-3 py-1.5 font-semibold">{v}</span>
                  ))}
                </div>
                <Link to="/products?category=vapes" data-testid="home-banner-shop-vapes">
                  <button className="mt-10 ha-btn bg-white text-ha-bg hover:bg-ha-ink group">
                    Shop Vapes
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </button>
                </Link>
              </div>
              {/* LIVE interactive STNDRD flyer via iframe */}
              <div className="relative aspect-[16/9] md:aspect-auto md:min-h-[560px]" data-testid="home-stndrd-live">
                <iframe
                  src="/stndrd-live.html"
                  title="STNDRD Live Interactive Vape Flyer"
                  className="absolute inset-0 w-full h-full block"
                  style={{ border: 0, background: '#000' }}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ HOUSE GUMMIES — object-contain so the whole flyer is visible ============ */}
      <section className="ha-container ha-cv-auto mt-12 md:mt-20" data-testid="home-gummies-banner">
        <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-center">
          <div className="md:col-span-5 space-y-6">
            <p className="ha-eyebrow" style={{ color: 'rgba(125,184,51,0.85)' }}>House gummies</p>
            <h2 className="ha-display text-ha-ink" style={{ fontSize: 'clamp(2.5rem, 6vw, 5.5rem)' }}>
              25mg each. <span className="ha-display-italic" style={{ color: '#7DB833' }}>$2 each.</span>
            </h2>
            <p className="font-body text-lg text-ha-ink/70 leading-relaxed max-w-md">
              House-made gummies. Nine flavors on rotation. Clean dose, clean taste. Perfect for the everyday session.
            </p>
            <Link to="/products?category=edibles">
              <button className="ha-btn ha-btn-green" data-testid="home-banner-shop-edibles">
                Shop Edibles
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
          <div className="md:col-span-7">
            <Link to="/products?category=edibles" className="block group relative" data-testid="home-gummies-banner-image">
              <div className="w-full flex items-center justify-center">
                <HomeBannerImage
                  src={GUMMIES_BANNER}
                  alt="House Gummies — 25mg each, $2 each"
                  label="House Gummies"
                  className="w-full h-auto max-h-[600px] object-contain group-hover:scale-[1.02] transition-transform duration-1000"
                />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ DAILY DEALS — FILL the frame with the graphic ============ */}
      <section className="ha-container ha-cv-auto mt-12 md:mt-20" data-testid="home-daily-deals-banner">
        <div className="text-center mb-8">
          <p className="ha-eyebrow mb-4" style={{ color: 'rgba(138,63,216,0.75)' }}>Daily Deals</p>
          <h2 className="ha-display text-ha-ink" style={{ fontSize: 'clamp(3rem, 8vw, 6.5rem)' }}>
            In-store <span className="ha-display-italic" style={{ color: '#8A3FD8' }}>only.</span>
          </h2>
          <p className="mt-5 font-body text-lg text-ha-ink/70 max-w-xl mx-auto">
            Happy hour 4:20-5:20 everyday. New deal every day of the week. Only at 570 Merrimon Ave &amp; 332 Weaverville Rd.
          </p>
        </div>
        {/* Fill container fully — flyer is 9:16 portrait, use aspect ratio 9:16 with object-cover */}
        <div className="relative max-w-[520px] mx-auto rounded-3xl overflow-hidden border border-ha-ink/10"
          style={{ boxShadow: '0 0 100px -30px rgba(138,63,216,0.5), 0 40px 80px -30px rgba(0,0,0,0.8)' }}>
          <div className="aspect-[9/16] bg-ha-bg overflow-hidden">
            <HomeBannerImage src={DAILY_DEALS_BANNER} alt="Daily Deals" label="Daily Deals" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="mt-8 flex justify-center">
          <Link to="/locations">
            <button className="ha-btn ha-btn-purple group">
              <Store className="w-4 h-4" />
              Visit the Shop
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </Link>
        </div>
      </section>

      {/* ============ DRINKS — TWO FULL FLYERS SIDE-BY-SIDE with DRAMATIC vertical split ============ */}
      <section className="ha-container ha-cv-auto mt-12 md:mt-20" data-testid="home-drinks-banner">
        <div className="text-center mb-10">
          <p className="ha-eyebrow mb-4" style={{ color: 'rgba(125,184,51,0.85)' }}>THC Drinks</p>
          <h2 className="ha-display text-ha-ink" style={{ fontSize: 'clamp(2.75rem, 6.5vw, 6rem)' }}>
            Two ways to <span className="ha-display-italic" style={{ color: '#7DB833' }}>sip.</span>
          </h2>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-0 items-stretch" data-testid="home-drinks-split">
          {/* LEFT — Higher potency (full image visible) */}
          <div className="relative">
            <Link
              to="/products?category=drinks"
              data-testid="home-drinks-higher"
              className="relative block group h-full"
            >
              {/* Section label above */}
              <div className="mb-4 text-center md:pr-8">
                <p className="ha-eyebrow" style={{ color: 'rgba(182,226,76,0.9)' }}>Left side</p>
                <h3
                  className="ha-display text-ha-ink mt-2"
                  style={{ fontSize: 'clamp(1.5rem, 3vw, 3rem)', lineHeight: '0.95' }}
                >
                  Higher potency.{' '}
                  <span className="ha-display-italic" style={{ color: '#B6E24C' }}>Harder hitting.</span>
                </h3>
              </div>
              <div className="w-full flex items-center justify-center md:pr-8">
                <img
                  src={DRINKS_HIGHER}
                  alt="Higher potency drinks — Uncle Skunks, Moonwater, Not Bored"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto max-h-[720px] object-contain group-hover:scale-[1.02] transition-transform duration-700"
                />
              </div>
            </Link>
          </div>

          {/* DRAMATIC vertical divider down the center (desktop only) */}
          <div
            className="hidden md:block pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 w-[2px] z-[3]"
            aria-hidden="true"
          >
            <div
              className="w-full h-full"
              style={{
                background:
                  'linear-gradient(to bottom, transparent 0%, rgba(125,184,51,0) 5%, #7DB833 20%, #FFFFFF 50%, #8A3FD8 80%, rgba(138,63,216,0) 95%, transparent 100%)',
                boxShadow: '0 0 24px 4px rgba(125,184,51,0.55), 0 0 48px 8px rgba(138,63,216,0.35)',
              }}
            />
          </div>

          {/* Mobile horizontal divider */}
          <div
            className="md:hidden my-8 h-[2px] w-full pointer-events-none"
            aria-hidden="true"
            style={{
              background:
                'linear-gradient(to right, transparent, #7DB833 30%, #FFFFFF 50%, #8A3FD8 70%, transparent)',
              boxShadow: '0 0 24px 4px rgba(125,184,51,0.5)',
            }}
          />

          {/* RIGHT — Lighter / zero calorie (full image visible) */}
          <div className="relative">
            <Link
              to="/products?category=drinks"
              data-testid="home-drinks-lighter"
              className="relative block group h-full"
            >
              <div className="mb-4 text-center md:pl-8">
                <p className="ha-eyebrow" style={{ color: 'rgba(211,166,255,0.95)' }}>Right side</p>
                <h3
                  className="ha-display text-ha-ink mt-2"
                  style={{ fontSize: 'clamp(1.5rem, 3vw, 3rem)', lineHeight: '0.95' }}
                >
                  Zero calorie.{' '}
                  <span className="ha-display-italic" style={{ color: '#D3A6FF' }}>Zero sugar.</span>
                </h3>
              </div>
              <div className="w-full flex items-center justify-center md:pl-8">
                <img
                  src={DRINKS_LIGHTER}
                  alt="Zero calorie zero sugar drinks — Blueberry Kush, Raspberry Lime, Mango Uplift"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto max-h-[720px] object-contain group-hover:scale-[1.02] transition-transform duration-700"
                />
              </div>
            </Link>
          </div>
        </div>

        <div className="mt-10 flex justify-center">
          <Link to="/products?category=drinks">
            <button className="ha-btn ha-btn-green group" data-testid="home-banner-shop-drinks">
              Shop All Drinks
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </Link>
        </div>
      </section>

      {/* ============ CONCENTRATES ============ */}
      <section className="ha-container ha-cv-auto mt-12 md:mt-20" data-testid="home-concentrates-band">
        <div className="rounded-3xl overflow-hidden bg-ha-card border border-ha-ink/10 p-8 md:p-14 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <p className="ha-eyebrow mb-4" style={{ color: 'rgba(138,63,216,0.75)' }}>Concentrates</p>
            <h2 className="ha-display text-ha-ink" style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}>
              Live <span className="ha-display-italic" style={{ color: '#8A3FD8' }}>rosin.</span>
            </h2>
            <p className="mt-5 font-body text-lg text-ha-ink/70 leading-relaxed max-w-md">
              Solventless rosin, badder, sugar &amp; sauce. Full-spectrum, small-batch, chef&apos;s-choice terps.
            </p>
          </div>
          <div className="md:col-span-5 md:text-right">
            <Link to="/products?category=concentrates">
              <button className="ha-btn ha-btn-purple group">
                Shop Concentrates
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ NEWSLETTER — COMPACT ============ */}
      <section className="ha-container mt-16 md:mt-24" data-testid="home-newsletter">
        <div className="relative overflow-hidden rounded-full border border-ha-ink/10 bg-ha-card">
          <div className="pointer-events-none absolute -left-16 top-1/2 -translate-y-1/2 w-40 h-40 rounded-full blur-3xl opacity-35" style={{ background: '#8A3FD8' }} />
          <div className="pointer-events-none absolute -right-16 top-1/2 -translate-y-1/2 w-40 h-40 rounded-full blur-3xl opacity-30" style={{ background: '#7DB833' }} />
          <div className="relative px-6 md:px-10 py-5 md:py-6 grid md:grid-cols-12 gap-4 md:gap-6 items-center">
            <div className="md:col-span-5">
              <p className="ha-eyebrow mb-1">Members-only</p>
              <h2 className="ha-display text-ha-ink" style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)' }}>
                Get <span className="ha-display-italic" style={{ color: '#7DB833' }}>20% off</span> your first order.
              </h2>
            </div>
            <div className="md:col-span-7">
              {newsletterSubmitted ? (
                <div data-testid="home-newsletter-success" className="rounded-full px-6 py-3 text-center bg-ha-green text-ha-bg font-body text-[12px] uppercase tracking-[0.28em] font-bold">
                  You&apos;re in. Check your inbox.
                </div>
              ) : (
                <form onSubmit={handleNewsletter} data-testid="home-newsletter-form" className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email" required value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Email address"
                    data-testid="home-newsletter-email"
                    className="flex-1 h-11 px-5 rounded-full bg-ha-bg border border-ha-ink/22 text-ha-ink placeholder:text-ha-ink/40 font-body text-sm focus:outline-none focus:border-ha-green focus:ring-2 focus:ring-ha-green/25 transition-all"
                  />
                  <button type="submit" data-testid="home-newsletter-submit" className="h-11 px-6 rounded-full font-body text-[11px] tracking-[0.24em] uppercase font-bold text-ha-bg whitespace-nowrap"
                    style={{ background: '#7DB833', boxShadow: '0 10px 30px -10px rgba(125,184,51,0.6)' }}>
                    Get 20% Off
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ============ CATEGORY GRID ============ */}
      <section className="ha-container ha-cv-auto mt-16 md:mt-24" data-testid="home-categories">
        <div className="flex items-end justify-between mb-10 gap-6">
          <div>
            <p className="ha-eyebrow mb-4">Shop by category</p>
            <h2 className="ha-display text-ha-ink" style={{ fontSize: 'clamp(2.5rem, 5.5vw, 5rem)' }}>The menu.</h2>
          </div>
          <Link to="/products" className="hidden md:inline-flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.28em] text-ha-ink/70 hover:text-ha-green transition-colors font-bold group">
            All products <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
          {CATEGORIES.map((c, i) => (
            <div key={c.key} className="ha-fade-in-up" style={{ animationDelay: `${i * 60}ms` }}>
              <Link to={`/products?category=${c.key}`} data-testid={`home-category-${c.key}`}
                className="group relative block h-52 md:h-72 overflow-hidden bg-ha-card text-ha-ink rounded-2xl border border-ha-ink/10 hover:border-ha-ink/40 transition-all duration-500 ha-product-card">
                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <c.Icon className="w-7 h-7 transition-colors" strokeWidth={1.5} style={{ color: c.accent }} />
                    <span className="font-body text-[10px] uppercase tracking-[0.28em] text-ha-ink/40 font-bold">0{i + 1}</span>
                  </div>
                  <div>
                    <p className="font-body text-[10px] uppercase tracking-[0.28em] text-ha-ink/50 mb-2 font-bold">{c.tagline}</p>
                    <p className="font-anton text-4xl md:text-5xl text-ha-ink uppercase tracking-wide leading-none">{c.label}</p>
                    <div className="mt-4 inline-flex items-center gap-2 font-body text-[10px] uppercase tracking-[0.28em] text-ha-ink/60 group-hover:text-ha-ink transition-colors font-bold">
                      Shop <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                  style={{ background: `radial-gradient(circle at 70% 30%, ${c.accent}22, transparent 60%)` }} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* ============ HOUSE RULE ============ */}
      <section className="relative border-y border-ha-ink/10 bg-ha-bg-2 ha-grain-surface ha-cv-auto mt-16 md:mt-24">
        <div className="ha-container ha-section grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <p className="ha-eyebrow mb-4">The house rule</p>
            <h3 className="ha-display text-ha-ink" style={{ fontSize: 'clamp(2.5rem, 5.5vw, 5rem)' }}>
              People over<br />profit. <span className="ha-display-italic" style={{ color: '#7DB833' }}>Always.</span>
            </h3>
          </div>
          <div className="md:col-span-7 md:pl-12 md:border-l border-ha-ink/22">
            <p className="font-editorial text-2xl md:text-3xl leading-snug text-ha-ink/85">
              We built High Achievers as a hemp house first, a business second. Every strain is chosen by someone who smokes it. Every price is set with real people — our neighbors, our regulars — in mind.
            </p>
            <p className="mt-6 font-body text-ha-ink/60 max-w-lg">
              If a product isn&apos;t good enough for our own rotation, it doesn&apos;t make the shelf. That&apos;s the rule.
            </p>
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.28em] text-ha-green font-bold hover:text-ha-ink transition-colors group">
              Read our story <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ REVIEWS ============ */}
      <section className="ha-cv-auto mt-16 md:mt-24" data-testid="home-reviews">
        <div className="ha-container pb-8">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-10">
            <div className="md:col-span-7">
              <p className="ha-eyebrow mb-4">Regulars &amp; first-timers</p>
              <h2 className="ha-display text-ha-ink" style={{ fontSize: 'clamp(2.5rem, 5.5vw, 5rem)' }}>
                What Asheville <span className="ha-display-italic" style={{ color: '#7DB833' }}>says.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:pl-10 md:border-l border-ha-ink/22">
              <div className="flex items-baseline gap-5">
                <span className="ha-display text-ha-ink ha-tabular ha-glow-green" style={{ fontSize: 'clamp(4rem, 8vw, 7rem)' }}>4.9</span>
                <div>
                  <div className="flex gap-0.5 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-ha-green text-ha-green" />
                    ))}
                  </div>
                  <p className="font-body text-sm text-ha-ink/60">Based on 2,400+ local reviews</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ReviewCarousel reviews={REVIEWS} />
        <div className="h-12 md:h-16" />
      </section>

      {/* ============ LOCATIONS ============ */}
      <section className="ha-container mt-16 md:mt-24" data-testid="home-locations">
        <div className="flex items-end justify-between mb-10 gap-6">
          <div>
            <p className="ha-eyebrow mb-4">Two doors, one house</p>
            <h2 className="ha-display text-ha-ink" style={{ fontSize: 'clamp(2.5rem, 5.5vw, 5rem)' }}>
              Visit us in <span className="ha-display-italic" style={{ color: '#8A3FD8' }}>Asheville.</span>
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {[
            { title: 'Merrimon Ave', addr: '570 Merrimon Ave, Asheville, NC 28804', img: 'https://customer-assets.emergentagent.com/job_366c63fb-24f7-4142-a1f4-17dbc5ce0a8a/artifacts/5bdomb13_IMG_1371.jpeg', maps: 'https://www.google.com/maps/search/?api=1&query=570+Merrimon+Ave+Asheville+NC+28804', accent: '#7DB833' },
            { title: 'Weaverville Rd', addr: '332 Weaverville Rd, Asheville, NC 28804', img: 'https://customer-assets.emergentagent.com/job_366c63fb-24f7-4142-a1f4-17dbc5ce0a8a/artifacts/79ddqgdk_IMG_1370.jpeg', maps: 'https://www.google.com/maps/search/?api=1&query=332+Weaverville+Rd+Asheville+NC+28804', accent: '#8A3FD8' },
          ].map((loc) => (
            <div key={loc.title} className="group relative overflow-hidden rounded-3xl border border-ha-ink/10 ha-product-card" style={{ boxShadow: `0 0 80px -35px ${loc.accent}, 0 40px 80px -30px rgba(0,0,0,0.7)` }}>
              <div className="aspect-[16/11] overflow-hidden">
                <img src={loc.img} alt={loc.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-1000" loading="lazy" decoding="async" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-ha-bg/95 via-ha-bg/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-10">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="ha-eyebrow" style={{ color: `${loc.accent}CC` }}>High Achievers</p>
                    <h3 className="ha-display text-ha-ink mt-2" style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)' }}>{loc.title}</h3>
                    <p className="mt-2 font-body text-sm text-ha-ink/85">{loc.addr}</p>
                    <p className="mt-1 font-body text-[10px] uppercase tracking-[0.28em] text-ha-ink/50 font-bold">Mon-Sat 9-9 · Sun 10-9</p>
                  </div>
                  <a href={loc.maps} target="_blank" rel="noopener noreferrer" className="shrink-0 ha-btn ha-btn-ink group">
                    Directions <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ CLOSING MARK ============ */}
      <section className="ha-container pb-20 md:pb-28 mt-16 md:mt-24" data-testid="home-closing-mark">
        <div className="border-t border-ha-ink/22 pt-16 md:pt-24 flex flex-col items-center text-center">
          <img src={LOGO_URL} alt="High Achievers" className="h-20 md:h-28 w-auto opacity-80" loading="lazy" />
          <p className="mt-6 ha-eyebrow">Asheville · Est. 2023</p>
          <p className="mt-4 font-editorial text-2xl md:text-3xl text-ha-ink/70 max-w-lg leading-snug">
            A hemp house. Nothing more, nothing less.
          </p>
        </div>
      </section>
    </div>
  );
}
